import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { FlocknoteAppState } from "Flocknote/state.js";

import API from "Signups/API.js";
import NewSignupButton from "Signups/SharedComponents/NewSignupButton";
import { useSignups } from "Signups/state.js";
import { motion } from "framer-motion";

import FnrButton, { ButtonIcon, ButtonText } from "UI/FnrButton3";
import { FnrToggle, FnrToggleChild, FnrToggleItems } from "UI/FnrToggle2";

import { useOnClickOutside } from "Utilities/Hooks/useOnClickOutside";

import FamilyRegistrationAdminButton from "./FamilyRegistrationAdminButton";
import "./SignupListsHolder.scss";
import SignupSummary from "./SignupSummary";

export default function SignupListsHolder({ signupListID, action }) {
  // At this point, the React app doesn't know when Flocknote navigates from /signups to other (non-React) pages in the app.
  // So we manually track location.key to help detect when we need to refresh the list of signups.
  const location = useLocation();
  const [prevLocationKey, setPrevLocationKey] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const containerRef = useRef();

  const [
    {
      allSignupLists,
      showingSignupsListHolder,
      showArchivedSignups,
      hasArchivedSignups,
      canViewFamilyRegistrationAdminPanel,
      familyRegistrationIsLive,
      needToUpdateSignupLists,
    },
    dispatch,
  ] = useSignups();
  const [{ composer }] = useContext(FlocknoteAppState);

  const signupListsLength = allSignupLists ? allSignupLists.length : 0;

  const shouldRefresh =
    location.pathname === "/signups" && location.key !== prevLocationKey;

  // console.log({
  //   needToUpdateSignupLists,
  //   shouldRefresh,
  //   pathname: location.pathname,
  //   key: location.key,
  //   prevLocationKey,
  // });

  const hideSignupsListHolder = () => {
    dispatch({ type: "Hide Signups List Holder" });
  };

  useOnClickOutside(containerRef, () => hideSignupsListHolder());

  useEffect(() => {
    if (!allSignupLists || shouldRefresh || needToUpdateSignupLists) {
      if (isFetching) return;

      const getSignupLists = API.getSignupLists();
      setIsFetching(true);
      getSignupLists.post().then((res) => {
        dispatch({
          type: "setSignupListsData",
          payload: res,
        });
        setIsFetching(false);
      });
    }
  }, [
    signupListsLength,
    allSignupLists,
    dispatch,
    shouldRefresh,
    isFetching,
    needToUpdateSignupLists,
  ]);

  useEffect(() => {
    setPrevLocationKey(location.key);
  }, [location.key]);

  useEffect(
    function showUnarchivedAfterDeletingLastArchivedSignup() {
      // If the user deletes their last/only archived Signup, the toggle UI disappears.
      // So we need to toggle the flag to make sure the list is back to showing unarchived Signups.
      if (
        showArchivedSignups &&
        allSignupLists?.length && // this check is necessary to make sure the lists have loaded before checking for presence of archived
        !hasArchivedSignups
      ) {
        dispatch({ type: "toggleShowArchivedSignups" });
      }
    },
    [dispatch, hasArchivedSignups, showArchivedSignups, allSignupLists]
  );

  if (!allSignupLists && isFetching && window.innerWidth >= 1480) {
    // show the signup lists holder area to visually center signup loading spinner
    return <div className="SignupListsHolder"></div>;
  }

  if (!allSignupLists) return null;

  const showSignupsListHolder = () => {
    dispatch({ type: "Show Signups List Holder" });
  };

  const toggleShowArchivedSignups = () => {
    dispatch({ type: "toggleShowArchivedSignups" });
  };

  const handleToggleClick = (btnName) => {
    if (btnName === "Active" && showArchivedSignups) {
      toggleShowArchivedSignups();
    }

    if (btnName === "Archived" && !showArchivedSignups) {
      toggleShowArchivedSignups();
    }
  };

  const animations = {
    initial: {
      x: -320,
    },
    animate: {
      x: -320,
    },
    transition: {
      delay: 0.1,
    },
  };

  if (
    allSignupLists.length > 0 &&
    ((!signupListID && !action) ||
      window.innerWidth >= 1480 ||
      (composer.minimized && window.innerWidth >= 1140))
  ) {
    animations.initial.x = 0;
    animations.animate.x = 0;
  }

  if (showingSignupsListHolder && allSignupLists.length > 0) {
    animations.animate.x = 0;
    animations.transition.delay = 0;
  }

  let hasSignupListItems = allSignupLists && allSignupLists.length > 0;

  return (
    <div
      className={`SignupListsContainer ${
        !hasSignupListItems ? "empty_signup_list" : ""
      }`}
    >
      {hasSignupListItems && (
        <div className="show_signups_button">
          <FnrButton onClick={showSignupsListHolder} size="small">
            <ButtonIcon fas fa-bars />
            <ButtonText>All Signups</ButtonText>
          </FnrButton>
        </div>
      )}

      {showingSignupsListHolder && signupListsLength > 0 && (
        <div
          className="hide_signups_button"
          onClick={hideSignupsListHolder}
        ></div>
      )}

      <motion.div
        className="SignupListsHolder"
        {...animations}
        ref={containerRef}
      >
        <NewSignupButton />

        {hasArchivedSignups && (
          <ArchivedSignupToggle>
            <FnrToggle style={{ border: "none", background: "none" }}>
              <FnrToggleItems>
                <FnrToggleChild selected={!showArchivedSignups}>
                  <ArchivedSignupToggleButton
                    onClick={() => handleToggleClick("Active")}
                  >
                    Active
                  </ArchivedSignupToggleButton>
                </FnrToggleChild>
                <FnrToggleChild selected={showArchivedSignups}>
                  <ArchivedSignupToggleButton
                    onClick={() => handleToggleClick("Archived")}
                  >
                    Archived
                  </ArchivedSignupToggleButton>
                </FnrToggleChild>
              </FnrToggleItems>
            </FnrToggle>
          </ArchivedSignupToggle>
        )}
        <div className="SignupLists">
          <div className="inside">
            <div className="lists">
              {familyRegistrationIsLive &&
                canViewFamilyRegistrationAdminPanel && (
                  <FamilyRegistrationAdminButton />
                )}
              {allSignupLists.length > 0 && (
                <>
                  {allSignupLists.map((signupList, i) => {
                    if (
                      (signupList.signupListStatusID === 1 &&
                        !showArchivedSignups) ||
                      (signupList.signupListStatusID === 4 &&
                        showArchivedSignups)
                    ) {
                      return (
                        <SignupSummary
                          key={signupList.ID}
                          index={i}
                          total={allSignupLists.length}
                          {...signupList}
                          isActive={signupList.signupListStatusID === 1}
                        />
                      );
                    } else {
                      return null;
                    }
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

const ArchivedSignupToggle = styled.div`
  padding: 0 20px;
  padding-left: 60px;
`;
const ArchivedSignupToggleButton = styled.div`
  text-align: center;
  padding: 4px 0;
`;
